/**
 * Cart Drawer Controller
 * Built on the shared Drawer class.
 * Handles AJAX quantity changes, line item removals, drawer refreshes, and count badges.
 */

document.addEventListener('DOMContentLoaded', function () {
  const drawerEl = document.getElementById('cart-drawer');
  if (!drawerEl) return;

  // Initialize shared drawer instance
  const drawer = new Drawer(drawerEl, {
    name: 'cart',
    id: 'cart-drawer',
  });

  // -------------------------------------------------------------
  // AJAX Cart Quantity & Line Item Removal Controller
  // -------------------------------------------------------------
  let isUpdating = false;

  async function updateCartQuantity(lineKey, quantity) {
    if (isUpdating || !lineKey) return;
    isUpdating = true;

    const itemEl = drawerEl.querySelector(`.cart-drawer__item[data-line-item-key="${lineKey}"]`);
    if (itemEl) {
      itemEl.classList.add('is-loading');
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: Math.max(0, parseInt(quantity, 10) || 0),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Cart update error:', data.description || data.message);
        if (itemEl) itemEl.classList.remove('is-loading');
        isUpdating = false;
        return;
      }

      await refreshDrawer();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
    } catch (err) {
      console.error('Network error updating cart:', err);
      if (itemEl) itemEl.classList.remove('is-loading');
    } finally {
      isUpdating = false;
    }
  }

  async function refreshDrawer() {
    try {
      const bodyEl = drawerEl.querySelector('.cart-drawer__body, .drawer__body');
      const scrollTop = bodyEl ? bodyEl.scrollTop : 0;

      const url =
        window.location.pathname +
        (window.location.search ? window.location.search + '&' : '?') +
        `_=${Date.now()}`;

      const res = await fetch(url);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('#cart-drawer');

      if (newDrawer) {
        const currentPanel = drawerEl.querySelector('.cart-drawer__panel, .drawer__panel');
        const newPanel = newDrawer.querySelector('.cart-drawer__panel, .drawer__panel');
        if (currentPanel && newPanel) {
          currentPanel.innerHTML = newPanel.innerHTML;
        }

        // Restore scroll position
        const restoredBody = drawerEl.querySelector('.cart-drawer__body, .drawer__body');
        if (restoredBody) {
          restoredBody.scrollTop = scrollTop;
        }

        // Update cart count badges everywhere
        const newCountEl = newDrawer.querySelector('[data-cart-drawer-count]');
        const countText = newCountEl ? newCountEl.textContent.trim() : '0';
        document.querySelectorAll('[data-cart-count], [data-cart-drawer-count]').forEach((badge) => {
          badge.textContent = countText;
          if (parseInt(countText, 10) === 0) {
            badge.classList.add('is-empty');
          } else {
            badge.classList.remove('is-empty');
          }
        });
      }
    } catch (err) {
      console.error('Error refreshing cart drawer:', err);
    }
  }

  // Delegated events for quantity changes & removal
  drawerEl.addEventListener('click', function (e) {
    // 1. Quantity button (+ / -)
    const qtyBtn = e.target.closest('.js-cart-qty-btn');
    if (qtyBtn) {
      e.preventDefault();
      const lineKey = qtyBtn.dataset.lineKey;
      const action = qtyBtn.dataset.action;
      const selector = qtyBtn.closest('.cart-drawer__quantity-selector');
      const input = selector ? selector.querySelector('.js-cart-qty-input') : null;
      if (!input || !lineKey) return;

      const currentVal = parseInt(input.value, 10) || 1;
      const newVal = action === 'plus' ? currentVal + 1 : currentVal - 1;
      updateCartQuantity(lineKey, newVal);
      return;
    }

    // 2. Remove item button
    const removeBtn = e.target.closest('.js-cart-remove-btn, [data-line-item-remove]');
    if (removeBtn) {
      e.preventDefault();
      const lineKey =
        removeBtn.dataset.lineKey ||
        removeBtn.closest('.cart-drawer__item')?.dataset.lineItemKey;
      if (lineKey) {
        updateCartQuantity(lineKey, 0);
      }
    }
  });

  // Manual quantity input change
  drawerEl.addEventListener('change', function (e) {
    const input = e.target.closest('.js-cart-qty-input');
    if (input) {
      const lineKey = input.dataset.lineKey;
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 0) val = 0;
      if (lineKey) {
        updateCartQuantity(lineKey, val);
      }
    }
  });

  // Global interface for external calls (e.g., Ajax add to cart)
  window.cartDrawer = {
    open: () => drawer.open(),
    close: () => drawer.close(),
    toggle: () => drawer.toggle(),
    refresh: refreshDrawer,
    updatePosition: () => drawer.updatePosition(),
    isOpen: () => drawer.isOpen,
    instance: drawer,
  };
});
